import {
  Links,
  Meta,
  Scripts,
  useLoaderData,
  Outlet,
  useMatches,
  useRouteError,
  isRouteErrorResponse,
} from '@remix-run/react';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { SiteLayout } from './components/site/SiteLayout';
import NotFoundPage from './components/NotFoundPage';
import { Styles, StylesType } from './components/Styles';
import { RootHandle } from './components/root/types';
import PermissionDeniedPage from './components/PermissionDeniedPage';
import LoginRequiredPage from './components/LoginRequiredPage';

export const WebbaseProdHost = 'api.gopipeline.io';

const isProd = process.env.NODE_ENV === 'production';
const isServer = typeof window === 'undefined';

type RootLoaderData = {
  env: { [key: string]: string | undefined };
};

export const loader = async (): Promise<RootLoaderData> => {
  return {
    env: {
      WEBBASE_HOST: process.env.WEBBASE_HOST,
      AMPLITUDE_API_KEY: process.env.AMPLITUDE_API_KEY,
      SENTRY_ADDON_DSN: process.env.SENTRY_ADDON_DSN,
      NODE_ENV: process.env.NODE_ENV || 'development',
    },
  };
};

function Document({
  children,
  data,
  styles,
  title,
}: {
  children: React.ReactNode;
  data: RootLoaderData;
  styles?: StylesType[];
  title?: string;
}) {
  // Routes can opt-in to exclude remix scripts (eg no hydration or client side react app)
  const fullHeight = !!useMatches()
    .filter((m) => m.handle)
    .find((m) => {
      const h = m.handle as RootHandle;
      return h.fullHeight === true;
    });

  return (
    <html lang="en" className={fullHeight ? 'h-full' : undefined}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {title && <title>{title}</title>}
        <Meta />
        <meta
          name="facebook-domain-verification"
          content="st5u69ixhsjkx6pphma0167sjjkp7a"
        />
        <Links />
        <link rel="icon" href="/favicon.png" type="image/png" />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(data.env)}`,
          }}
        />
        {/* install a dummy require() function to satisfy connection-string-parser */}
        <script
          dangerouslySetInnerHTML={{
            __html: `function require() { throw new Error('this is a stub to satisfy esbuild; do not call'); }`,
          }}
        />
        {styles && (
          <>
            {styles.map((css, index) => (
              <link key={index} rel={css.rel} href={css.href} />
            ))}
          </>
        )}
      </head>
      <body className={fullHeight ? 'h-full' : undefined}>
        {children}

        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  return (
    <Document data={data}>
      <Outlet />
    </Document>
  );
}

export default App;

const WebbaseHost = isServer ? process.env.WEBBASE_HOST : 'api.gopipeline.io';

const getPlaceholderLoaderData = () => {
  // we can't use `useLoaderData` if the current error occured in a LoaderFunction, so we hack in some values instead
  const data: RootLoaderData = {
    env: {
      NODE_ENV: isServer
        ? process.env.NODE_ENV || 'development'
        : 'development',
      APP: 'syncwith',
      AMPLITUDE_API_KEY: isServer
        ? process.env.AMPLITUDE_API_KEY
        : '857a897139d463fe34e62676d2cb2f8f',
      WEBBASE_HOST: WebbaseHost,
      SENTRY_ADDON_DSN: isServer
        ? process.env.SENTRY_ADDON_DSN
        : 'https://b55344c5f7ec45d193fc8cf952460b63@o522466.ingest.sentry.io/5880196',
    },
  };

  return data;
};

export function ErrorBoundary() {
  const error = useRouteError();
  // we can't use `useLoaderData` if the current error occured in a LoaderFunction, so we hack in some values instead
  const data = getPlaceholderLoaderData();

  if (!isProd) {
    console.error('Root ERROR:', error);
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <Document data={data} styles={Styles.app} title={'Not found'}>
        <NotFoundPage />
      </Document>
    );
  }
  if (isRouteErrorResponse(error) && error.status === 403) {
    return (
      <Document data={data} styles={Styles.app} title={'Permission denied'}>
        <PermissionDeniedPage error={error} />
      </Document>
    );
  }
  if (isRouteErrorResponse(error) && error.status === 401) {
    return (
      <Document data={data} styles={Styles.app} title={'Login required'}>
        <LoginRequiredPage error={error} />
      </Document>
    );
  }

  // see: https://docs.sentry.io/platforms/javascript/guides/remix/manual-setup/#v2-errorboundary
  captureRemixErrorBoundaryError(error);

  return (
    <Document data={data} styles={Styles.app} title={'Error'}>
      <SiteLayout>
        <h1>Sorry something went wrong!</h1>
        <p className="my-4">
          Oops, something unexpected happened. We have been notified and will
          look into it.
        </p>
      </SiteLayout>
    </Document>
  );
}
